'use client';

import React, { FC } from 'react';
import styles from './SearchSection.module.scss';

import { useInView } from 'react-intersection-observer';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import Label from '@/src/components/ui/label/Label';
import NextImage from '@/src/components/ui/nextImage/NextImage';
import AdvantageList from '@/src/components/sections/advantageList/AdvantageList';
import { advSearchFull } from '@/src/lib/staticObjects/advInfo';
import { ITheme } from '@/src/lib/types/themeInterface';
import ShopifyStoreButton from '@/src/components/ui/shopifyButton/ShopifyStoreButton';

type TPage = {
  theme: ITheme;
};

const heading = 'Powerful Product Search';
const heading2 = 'Launch Your Sales Booster Today';
const excerpt =
  'Drive sales with a robust product search that improves navigation and speeds up purchase decisions.';

const SearchSection: FC<TPage> = ({ theme }) => {
  const { ref: inViewRef2, inView: inView2 } = useInView({
    triggerOnce: true,
    rootMargin: '-250px 0px 0px',
    threshold: 0,
  });

  const { ref: inViewRef3, inView: inView3 } = useInView({
    triggerOnce: true,
    rootMargin: '-250px 0px 0px',
    threshold: 0,
  });

  return (
    <section className={styles.searchSection}>
      <Wrapper>
        <div className={styles.searchSection__main} ref={inViewRef2}>
          <div className={styles.searchSection__heading} ref={inViewRef2}>
            <Label>Search</Label>
            <h2 className={`heading__2 ${styles.heading} ${inView2 && styles.animate__heading}`}>
              {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
            </h2>
            <p className={`excerpt ${styles.excerpt} ${inView2 && styles.animate__excerpt}`}>
              {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
            </p>
          </div>

          <div className={styles['searchSection__main-pic']}>
            <NextImage
              src="/images/themes/stockist/search/main.webp"
              alt="MainSearch"
              fill
              sizes="(max-width: 480px) 100vw, (max-width: 1280px) 60vw, 85vw"
              loading="lazy"
            />
          </div>
        </div>

        <AdvantageList advList={advSearchFull} middle />

        <div className={styles['searchSection__banner']}>
          <div className={styles['searchSection__banner-about']} ref={inViewRef3}>
            <h2 className={`heading__2 ${styles.heading} ${inView3 && styles.animate__heading}`}>
              {createAnimationArr(heading2, styles.word__wrapper, 0, 0.01)}
            </h2>

            <div className={styles['searchSection__banner-text']}>
              Get the powerful theme designed to maximize conversions, engage shoppers, and turn
              visitors into loyal customers.
            </div>

            <div className={styles['searchSection__banner-actions']}>
              <ShopifyStoreButton isWhite={true} theme={theme} />
            </div>
          </div>

          <div className={styles['searchSection__banner-pic']}>
            <NextImage
              src="/images/themes/stockist/search/banner-tablet.webp"
              alt="BannerTabletSearch"
              fill
              sizes="100vw"
              loading="lazy"
            />
            <NextImage
              src="/images/themes/stockist/search/banner.webp"
              alt="BannerSearch"
              fill
              sizes="(max-width: 480px) 100vw, (max-width: 1280px) 50vw, 80vw"
              loading="lazy"
            />
          </div>
        </div>
      </Wrapper>
    </section>
  );
};

export default SearchSection;
